import { Controller } from "@hotwired/stimulus"

// Salary Form Controller
// Handles dynamic form sections based on employment type (permanent vs contract)
// Calculates FTE-adjusted salary and rate conversions
export default class extends Controller {
  static targets = [
    "permanentSection", "contractSection", "permanentOnlyFields",
    "baseSalary", "fteSlider", "fteValue", "effectiveSalary",
    "rateType", "rateTypeLabel", "rateInput",
    "hourlyConversion", "dailyConversion", "annualConversion",
    "hourlyHidden", "dailyHidden"
  ]

  static values = {
    employmentType: { type: String, default: "permanent" },
    // Conversion constants: 8h/day, 5 days/week, 45 weeks/year
    hoursPerDay: { type: Number, default: 8 },
    daysPerWeek: { type: Number, default: 5 },
    weeksPerYear: { type: Number, default: 45 }
  }

  connect() {
    // Listen for role selection events from autocomplete
    this.element.addEventListener("role:selected", this.handleRoleSelected.bind(this))
    this.element.addEventListener("role:cleared", this.handleRoleCleared.bind(this))

    // Initialize display
    this.updateSectionVisibility()
    this.updateEffectiveSalary()
  }

  handleRoleSelected(event) {
    const { employmentType } = event.detail
    this.employmentTypeValue = employmentType
    this.updateSectionVisibility()
  }

  handleRoleCleared() {
    this.employmentTypeValue = "permanent"
    this.updateSectionVisibility()
  }

  updateSectionVisibility() {
    const isPermanent = this.employmentTypeValue === "permanent"

    if (this.hasPermanentSectionTarget) {
      this.permanentSectionTarget.classList.toggle("hidden", !isPermanent)
    }

    if (this.hasContractSectionTarget) {
      this.contractSectionTarget.classList.toggle("hidden", isPermanent)
    }

    // Hide bonus and KiwiSaver fields for contract roles
    if (this.hasPermanentOnlyFieldsTarget) {
      this.permanentOnlyFieldsTarget.classList.toggle("hidden", !isPermanent)
    }

    // Clear irrelevant fields when switching
    if (isPermanent && this.hasRateInputTarget) {
      this.rateInputTarget.value = ""
      this.updateRateConversions()
    } else if (!isPermanent && this.hasBaseSalaryTarget) {
      // Don't clear base salary - some people switching might want to keep value
    }
  }

  // FTE Slider handling
  updateFte(event) {
    const ftePercent = parseInt(event.target.value, 10)

    if (this.hasFteValueTarget) {
      this.fteValueTarget.textContent = `${ftePercent}%`
    }

    this.updateEffectiveSalary()
  }

  updateEffectiveSalary() {
    if (!this.hasBaseSalaryTarget || !this.hasFteSliderTarget || !this.hasEffectiveSalaryTarget) {
      return
    }

    const baseSalary = parseFloat(this.baseSalaryTarget.value) || 0
    const ftePercent = parseInt(this.fteSliderTarget.value, 10) || 100

    if (baseSalary > 0 && ftePercent < 100) {
      const effective = Math.round(baseSalary * ftePercent / 100)
      this.effectiveSalaryTarget.textContent = `Effective: $${this.formatNumber(effective)}/year`
      this.effectiveSalaryTarget.classList.remove("hidden")
    } else {
      this.effectiveSalaryTarget.classList.add("hidden")
    }
  }

  // Rate type handling for contracts
  updateRateType(event) {
    this.updateRateConversions()
  }

  updateRateConversions() {
    if (!this.hasRateInputTarget) return

    const rate = parseFloat(this.rateInputTarget.value) || 0
    const rateType = this.getRateType()

    // Calculate all rates based on input type
    const hoursPerYear = this.hoursPerDayValue * this.daysPerWeekValue * this.weeksPerYearValue // 1800
    const daysPerYear = this.daysPerWeekValue * this.weeksPerYearValue // 225

    let hourly, daily, annual

    if (rateType === "hourly") {
      hourly = rate
      daily = rate * this.hoursPerDayValue
      annual = rate * hoursPerYear
    } else if (rateType === "daily") {
      daily = rate
      hourly = rate / this.hoursPerDayValue
      annual = rate * daysPerYear
    } else if (rateType === "annual") {
      annual = rate
      hourly = rate / hoursPerYear
      daily = rate / daysPerYear
    }

    // Update hidden fields for form submission
    this.updateHiddenFields(hourly, daily)

    // Update conversion displays
    if (rate > 0) {
      if (this.hasHourlyConversionTarget && rateType !== "hourly") {
        this.hourlyConversionTarget.textContent = `$${this.formatNumber(hourly)}/hour`
        this.hourlyConversionTarget.classList.remove("hidden")
      } else if (this.hasHourlyConversionTarget) {
        this.hourlyConversionTarget.classList.add("hidden")
      }

      if (this.hasDailyConversionTarget && rateType !== "daily") {
        this.dailyConversionTarget.textContent = `$${this.formatNumber(daily)}/day`
        this.dailyConversionTarget.classList.remove("hidden")
      } else if (this.hasDailyConversionTarget) {
        this.dailyConversionTarget.classList.add("hidden")
      }

      if (this.hasAnnualConversionTarget && rateType !== "annual") {
        this.annualConversionTarget.textContent = `$${this.formatNumber(annual)}/year`
        this.annualConversionTarget.classList.remove("hidden")
      } else if (this.hasAnnualConversionTarget) {
        this.annualConversionTarget.classList.add("hidden")
      }
    } else {
      // Hide all conversions when no rate entered
      if (this.hasHourlyConversionTarget) this.hourlyConversionTarget.classList.add("hidden")
      if (this.hasDailyConversionTarget) this.dailyConversionTarget.classList.add("hidden")
      if (this.hasAnnualConversionTarget) this.annualConversionTarget.classList.add("hidden")
    }
  }

  getRateType() {
    // Find the checked radio button for rate type
    const checkedRadio = this.element.querySelector('input[name="rate_type"]:checked')
    return checkedRadio ? checkedRadio.value : "hourly"
  }

  updateHiddenFields(hourly, daily) {
    // Update hidden fields by ID since they're outside the controller's target scope
    const hourlyHidden = document.getElementById("hourly_rate_hidden")
    const dailyHidden = document.getElementById("daily_rate_hidden")

    if (hourlyHidden) {
      hourlyHidden.value = hourly ? hourly.toFixed(2) : ""
    }
    if (dailyHidden) {
      dailyHidden.value = daily ? daily.toFixed(2) : ""
    }
  }

  formatNumber(num) {
    return new Intl.NumberFormat("en-NZ", {
      maximumFractionDigits: 0
    }).format(Math.round(num))
  }
}
